import clsx from 'clsx';
import { RefObject, useCallback } from 'react';
import { useOpenTrigger } from 'hooks/useOpenTrigger';
import { useRegion } from 'context/region';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';

import { IRegionItem } from 'constants/types';
import { useCountriesQuery } from 'hooks/api/countries';

import { Icon } from 'common/icon';
import { SelectArrow } from 'components/modal-arrow';
import { Analytics } from 'constants/analytics';
import { Routes } from 'constants/routes';
import Link from 'next/link';
import { useOnClickOutside } from 'usehooks-ts';
import {
  clearRefferer,
  clearUtm,
  saveCurrentRegion,
  saveRefferer,
  setRegionManuallySelected,
} from 'utils/storage';
import styles from './styles.module.css';

const AllRegionsItem = {
  image: null,
  name: 'All Regions',
  code: null,
  _id: null,
  slug: null,
  imageUrl:
    'https://d11o05pl2tdcd2.cloudfront.net/countries/1708125566357-918795276-nmez-rrl1.webp',
} as IRegionItem;

const Modal = (state: any) => {
  const router = useRouter();
  const modalRef = useRef<HTMLDivElement>(null);
  const { region: currentRegion, setRegion } = useRegion();

  const handleClose = useCallback(() => {
    if (state.state.close) {
      state.state.close();
    }
  }, [state.state]);

  useOnClickOutside(modalRef as RefObject<HTMLElement>, handleClose, 'mouseup');

  const { data = [] } = useCountriesQuery();

  const handleLanguageChange = useCallback(
    ({ _id }: { _id: string | null }) => {
      saveCurrentRegion(_id ?? null);
      setRegion(_id ?? null);
      setRegionManuallySelected(true);
      if (state.state.close) {
        state.state.close();
      }
      if (router.pathname === '/') {
        const { reg, ...query } = router.query;

        router.replace(
          {
            pathname: router.pathname,
            query: _id
              ? {
                  ...query,
                  reg: data?.find((item) => item._id === _id)?.slug || _id,
                }
              : query,
          },
          undefined,
          { shallow: false }
        );
      }
    },
    [data, router, setRegion, state.state]
  );

  const checkSelected = useCallback(
    ({ item }: { item: IRegionItem }) => {
      if (currentRegion === item._id) {
        return true;
      }
      if (currentRegion === null && item.name === AllRegionsItem.name) {
        return true;
      }
      if (currentRegion === 'null' && item.name === AllRegionsItem.name) {
        return true;
      }

      return false;
    },
    [currentRegion]
  );

  const regionsList = useMemo(() => {
    return [AllRegionsItem, ...data]?.map((item: IRegionItem, index) => (
      <li
        id={Analytics.banner.allRegionsRegion(item.slug ?? 'null')}
        key={(item?._id ?? 'null')?.toString() + index}
        className={clsx(styles.regionsItem, {
          [styles.regionsItemActive]: checkSelected({ item }),
        })}
        onClick={() => handleLanguageChange(item)}
      >
        {item?.imageUrl && (
          <Image
            src={item?.imageUrl}
            width={21}
            height={15}
            alt={item.name + ' Selected'}
          />
        )}
        <span>{item.name}</span>
      </li>
    ));
  }, [checkSelected, data, handleLanguageChange]);

  return (
    <div
      className={clsx(styles.regionsBody, {
        [styles.regionsBodyActive]: state.state.isOpen,
      })}
    >
      <div className={styles.container}>
        <div className={styles.regionsWrapper} ref={modalRef}>
          <div className={styles.regionsTop}>
            <div className={styles.regionsTitle}>
              <Icon.Flag fillColor="white" />
              <span>{'Regions'}</span>
            </div>
            <div
              className={styles.regionsCloseButton}
              onClick={state.state.close}
            >
              <Icon.Close />
            </div>
          </div>
          <div className={styles.regionsContainer}>
            <ul className={styles.regionsItems}>{regionsList}</ul>
            <p className={styles.cookieAgreementText}>
              When you change region, you accept{' '}
              <Link
                href={Routes.cookie}
                className={clsx(
                  styles.cookieAgreementText,
                  styles.cookieAgreementLink
                )}
              >
                cookie policy
              </Link>
            </p>
          </div>
          <div className={styles.regionsBottom}>
            <div className={styles.regionsFindLoc}>
              {'Can find your location?'}
            </div>

            <span
              className={styles.regionsContact}
              onClick={() => router.push(Routes.contactUs)}
            >
              {'contact us'}
              <Icon.Arrow />
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

interface RegionModalProps {
  useSlug?: boolean;
  textClassName?: string;
}

export const RegionsModal = ({
  useSlug = false,
  textClassName,
}: RegionModalProps) => {
  const router = useRouter();
  const [isButtonOverlayed, setButtonOverlayed] = useState(false);
  const {
    region: selectedRegion,
    setRegion,
    isRegionModalRequired,
    setRegionModalRequired,
  } = useRegion();

  const { data, isFetching } = useCountriesQuery();

  const [isOpen, setOpen] = useState(false);
  const state = useOpenTrigger({ isOpen, setOpen });

  const current = useMemo(() => {
    return (
      data?.find((item: IRegionItem) => item._id === selectedRegion) ||
      data?.find((item: IRegionItem) => item.slug === selectedRegion) ||
      AllRegionsItem
    );
  }, [selectedRegion, data]);

  useEffect(() => {
    if (isRegionModalRequired) {
      setOpen(true);
      setRegionModalRequired(false);
    }
  }, [isRegionModalRequired, setRegionModalRequired]);

  useEffect(() => {
    if (router.query.reg) {
      const region = data?.find(
        (item: IRegionItem) => item.slug === router.query.reg
      );

      if (region) {
        saveCurrentRegion(region._id);
        setRegion(region._id);

        if (router.pathname !== '/') {
          const { reg, ...query } = router.query;
          router.replace(
            {
              pathname: router.pathname,
              query,
            },
            undefined,
            { shallow: false }
          );
        }
      }
    }
  }, [data, router, setRegion, router?.query?.reg]);

  // Save Session Source
  useEffect(() => {
    const referrer = document.referrer;

    clearRefferer();

    if (referrer) {
      saveRefferer(referrer);
    }
  }, []);

  // Save UTM
  useEffect(() => {
    if (
      router.query.utm_source ||
      router.query.utm_medium ||
      router.query.utm_campaign ||
      router.query.utm_content ||
      router.query.utm_term
    ) {
      const {
        utm_source,
        utm_medium,
        utm_campaign,
        utm_content,
        utm_term,
        ...query
      } = router.query;

      clearUtm();

      const utmRecord: Record<string, string> = {};
      if (utm_source) {
        utmRecord.utm_source = utm_source?.toString();
      }
      if (utm_medium) {
        utmRecord.utm_medium = utm_medium?.toString();
      }
      if (utm_campaign) {
        utmRecord.utm_campaign = utm_campaign?.toString();
      }
      if (utm_content) {
        utmRecord.utm_content = utm_content?.toString();
      }
      if (utm_term) {
        utmRecord.utm_term = utm_term?.toString();
      }

      router.replace(
        {
          pathname: router.pathname,
          query,
        },
        undefined,
        { shallow: false }
      );
    }
  }, [
    router,
    router.query.utm_source,
    router.query.utm_medium,
    router.query.utm_campaign,
    router.query.utm_content,
    router.query.utm_term,
  ]);

  useEffect(() => {
    if (!router.query.reg && selectedRegion && current && current._id) {
      if (router.pathname === '/') {
        router.replace(
          {
            pathname: router.pathname,
            query: {
              ...router.query,
              reg: current?.slug ?? '',
            },
          },
          undefined,
          { shallow: false }
        );
      }
    }
  }, [current, router, selectedRegion]);

  useEffect(() => {
    if (state.isOpen) {
      setButtonOverlayed(true);
    } else {
      setTimeout(() => {
        setButtonOverlayed(false);
      }, 500);
    }
  }, [state.isOpen]);

  if (isFetching) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <button
        className={styles.regionsButton}
        onClick={isButtonOverlayed ? state.close : state.open}
      >
        {current?.imageUrl && (
          <Image
            width={16}
            height={11}
            alt={current.name + ' Selected'}
            src={current?.imageUrl}
          />
        )}
        {!useSlug && (
          <span className={clsx(styles.regionName, textClassName)}>
            {current?.name}
          </span>
        )}
        {useSlug && (
          <span className={styles.regionSlug}>{current?.slug ?? ''}</span>
        )}
        <div className={styles['arrow-container']}>
          <SelectArrow size="small" type="solid" isOpen={false} />
        </div>
      </button>

      <Modal state={state} />
    </>
  );
};
